import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Stepper,
    Step,
    StepLabel,
    Button,
    Box,
    Paper
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import BasicInfoForm from '../components/profile/setup/BasicInfoForm';
import PreferencesForm from '../components/profile/setup/PreferencesForm';
import LifestyleForm from '../components/profile/setup/LifestyleForm';

function ProfileSetup() {
    const navigate = useNavigate();
    const { currentUser, updateProfile } = useAuth();
    const { showNotification } = useNotification();
    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // プロフィール設定完了済みの場合はプロフィールページにリダイレクト
    useEffect(() => {
        if (currentUser?.isProfileSetupCompleted) {
            showNotification('プロフィール初期設定は既に完了しています', 'info');
            navigate('/profile');
            return;
        }
    }, [currentUser, navigate, showNotification]);

    const [profileData, setProfileData] = useState({
        profile: {
            nickname: '',
            dateOfBirth: null,
            gender: '',
            occupation: '',
            introduction: '',
            profileImage: null,
            sameGenderOnly: false,
            matchingPreferences: {
                smoking: 'non-smoker',
                pets: 'no',
                lifeRhythm: 'morning',
                cleanliness: 'clean',
                communication: 'active',
                stayingHome: 'often',
                visitors: 'welcome'
            },
            preferences: {
                rentRange: {
                    min: 50000,
                    max: 150000
                },
                preferredAreas: [{
                    prefecture: '',
                    city: '',
                    station: '',
                    line: ''
                }]
            },
            stayDuration: 'medium',
            moveInPreference: 'within 3 months'
        }
    });

    const steps = ['基本情報', '希望条件', 'ライフスタイル'];

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep((prevStep) => prevStep + 1);
        } else {
            showNotification('必須項目を入力してください', 'error');
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const validateStep = (step) => {
        const profile = profileData.profile;
        switch (step) {
            case 0: // 基本情報
                return !!(
                    profile.nickname &&
                    profile.dateOfBirth &&
                    profile.gender
                );
            case 1: // 希望条件
                return !!(
                    profile.preferences.rentRange.min &&
                    profile.preferences.rentRange.max &&
                    profile.preferences.preferredAreas[0].prefecture
                );
            case 2: // ライフスタイル
                return !!(
                    profile.matchingPreferences.lifeRhythm &&
                    profile.matchingPreferences.cleanliness &&
                    profile.matchingPreferences.communication &&
                    profile.matchingPreferences.stayingHome &&
                    profile.matchingPreferences.visitors
                );
            default:
                return true;
        }
    };

    // 全てのステップのバリデーションを行う
    const validateForm = () => {
        // 全てのステップが正しく入力されているか確認
        for (let i = 0; i < steps.length; i++) {
            if (!validateStep(i)) {
                showNotification(`「${steps[i]}」に必須項目が入力されていません`, 'error');
                setActiveStep(i); // 問題のあるステップに移動
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        // 必須項目のチェック
        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);

            // プロフィール情報と完了フラグを同時に更新
            const response = await fetch('/api/users/profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    profile: {
                        ...profileData.profile,
                        preferences: {
                            ...profileData.profile.preferences,
                            lifestyle: undefined // 古いlifestyleフィールドを削除
                        }
                    },
                    completeSetup: true // プロフィール設定完了フラグを同時に設定
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'プロフィールの更新に失敗しました');
            }

            const updatedProfile = await response.json();

            // AuthContextの状態を更新
            await updateProfile({ profile: updatedProfile.profile });

            showNotification('プロフィールを設定しました', 'success');

            // 状態更新の完了を待ってからナビゲート
            setTimeout(() => {
                navigate('/profile');
            }, 100);

        } catch (error) {
            console.error('プロフィール設定エラー:', error);
            showNotification(error.message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <BasicInfoForm
                        data={profileData}
                        onChange={setProfileData}
                    />
                );
            case 1:
                return (
                    <PreferencesForm
                        data={profileData}
                        onChange={setProfileData}
                    />
                );
            case 2:
                return (
                    <LifestyleForm
                        data={profileData}
                        onChange={setProfileData}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {renderStepContent(activeStep)}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    {activeStep > 0 && (
                        <Button onClick={handleBack} sx={{ mr: 1 }}>
                            戻る
                        </Button>
                    )}
                    {activeStep === steps.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? '送信中...' : '設定を完了'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                        >
                            次へ
                        </Button>
                    )}
                </Box>
            </Paper>
        </Container>
    );
}

export default ProfileSetup; 